import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authorizeAction, listCancellationReasons } from "@/lib/orderApi";
import { HALF_QTY, MIN_QTY, fmtQty, roundQty } from "@/lib/qty";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Every dialog in the app.
 *
 * `role="dialog"` and `aria-modal` were already here, but nothing held focus: with
 * the printer editor open, Tab walked straight out into the registry behind it and
 * a screen reader was told nothing about which control it had landed on. The trap,
 * the Escape key and the focus restore live here rather than in each caller so that
 * every dialog — printer editor, quantity pad, split, comp, khata — behaves the same.
 *
 * `aria-labelledby` points at the title the header already renders, so the dialog
 * announces itself with the same words it shows.
 */
/*
 * Anatomy (DESIGN.md §6.3): a pinned header (title, optional one-line
 * `description`, close), ONE scrolling body, and — when `footer` is given — a
 * pinned action footer, so Save never scrolls out of reach. `size` picks the width:
 * "standard" 480px, "wide" 640px, "confirm" 400px. Callers that still put their
 * buttons inside `children` render exactly as before.
 */
export function Modal({ title, description, children, footer, onClose, testId = "modal", size = "standard" }) {
  const panel = useRef(null);
  const restoreTo = useRef(null);
  const titleId = `${testId}-title`;

  useEffect(() => {
    restoreTo.current = document.activeElement;
    // The PANEL, not its first control. This is a till: the first control of the
    // payment sheet is "Cash", and arming Enter to settle a bill the instant the
    // sheet opens is exactly the kind of accident this codebase does not make.
    // Tab from here still walks into the dialog, in DOM order.
    panel.current?.focus?.();
    const previous = restoreTo.current;
    return () => previous?.focus?.();
  }, []);

  const onKeyDown = event => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose?.();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...(panel.current?.querySelectorAll(FOCUSABLE) || [])];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    // Backwards out of the first control — or out of the panel itself, which is
    // where focus starts — wraps to the last. Forwards off the end wraps to the
    // first. Either way the tab ring never reaches the page behind the dialog.
    if (event.shiftKey && (active === first || !focusable.includes(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal-backdrop" data-testid={`${testId}-backdrop`} onKeyDown={onKeyDown}>
      <section className="pos-modal" data-size={size} data-testid={testId} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? `${testId}-description` : undefined} tabIndex={-1} ref={panel}>
        <header>
          <div className="min-w-0">
            <h2 id={titleId} data-testid={titleId}>{title}</h2>
            {description && <p className="pos-modal-desc" id={`${testId}-description`}>{description}</p>}
          </div>
          <button type="button" className="pos-modal-close" onClick={onClose} data-testid={`${testId}-close-button`} aria-label="Close"><X className="w-4 h-4"/></button>
        </header>
        <div className="pos-modal-body">{children}</div>
        {footer && <footer className="pos-modal-foot" data-testid={`${testId}-footer`}>{footer}</footer>}
      </section>
    </div>
  );
}

const detailOf = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || "Request rejected";
  return "Could not reach the server.";
};

/**
 * Void the whole order.
 *
 * Lives here, and not on either page, because both screens need exactly the same
 * conversation and it is the kind that must not diverge: the till reaches it from
 * the cart, CaptainPAD from the running order, and one of them quietly dropping
 * the manager prompt would be a hole rather than a difference.
 *
 * Three things it insists on:
 *
 * * a reason — the server refuses without one, and a cancelled order with no reason
 *   is the row an owner cannot interpret a week later. The picker is filtered to
 *   whole-order reasons (`applies_to` is 'order' or 'both'); "Wrong item entered"
 *   explains a line, never a table walking out;
 * * a manager, once a KOT has been fired — `order_transitions` makes kot_sent →
 *   cancelled admin-only, and this is where a captain's tablet carries the
 *   manager's own employee id and PIN. The employee id is typed too: a PIN-only
 *   field would make the server search every user for a matching four digits;
 * * a deliberate press — `lastLine` opens this same dialog straight after the final
 *   live line is voided, because that is the moment the order becomes a zombie
 *   holding its table, and it is dismissible because "keep the order open" is a
 *   real answer (the guests are re-ordering).
 */
export function CancelOrderDialog({ order, token, busy, requiresManager, lastLine, onConfirm, onClose }) {
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let live = true;
    listCancellationReasons(token)
      .then(rows => {
        if (!live) return;
        // 'item' reasons are for a single line; 'order' and 'both' belong here.
        const scoped = (rows || []).filter(row => row.applies_to !== "item");
        setReasons(scoped);
        setReasonId(scoped[0] ? String(scoped[0].id) : "");
      })
      // No list is not a dead end — the free-text box still satisfies the server.
      .catch(() => live && setReasons([]));
    return () => {
      live = false;
    };
  }, [token]);

  const submit = async () => {
    setError(null);
    if (!reasonId && !note.trim()) {
      return setError("Say why this order is being voided — it goes on the audit trail.");
    }
    onConfirm({
      reasonCode: reasonId ? Number(reasonId) : null,
      reason: note.trim() || null,
      authorizedBy: null,
    });
  };

  return <Modal title={`Cancel order #${order.orderNumber}`} onClose={onClose} testId="cancel-order-modal">
    <p className="hint" data-testid="cancel-order-context">
      {lastLine
        ? "That was the last live line on this order. Void the whole order and free the table?"
        : "Every line goes and the table is freed. Lines already with the kitchen get a VOID slip at their station."}
    </p>

    {reasons.length > 0 && (
      <label>
        Reason
        <select value={reasonId} onChange={event => setReasonId(event.target.value)} data-testid="cancel-order-reason-select">
          {reasons.map(reason => <option value={reason.id} key={reason.id}>{reason.label}</option>)}
        </select>
      </label>
    )}

    <label>
      Note {reasons.length > 0 ? "(optional)" : ""}
      <Input
        placeholder="What happened?"
        value={note}
        onChange={event => setNote(event.target.value)}
        data-testid="cancel-order-note-input"
      />
    </label>

    {error && <p className="form-error" data-testid="cancel-order-error">{error}</p>}

    <div className="discount-actions">
      <button onClick={onClose} data-testid="cancel-order-keep-button">
        {lastLine ? "Keep the order open" : "Back"}
      </button>
      <Button variant="destructive" onClick={submit} disabled={busy || working} data-testid="confirm-cancel-order-button">
        Cancel order #{order.orderNumber}
      </Button>
    </div>
  </Modal>;
}

export function VariantModal({ item, onSelect, onClose }) {
  return <Modal title={`Choose ${item.name}`} onClose={onClose} testId="variant-selection-modal"><div className="variant-list">{item.variants.map(v => <Button key={v.name} variant="outline" onClick={() => onSelect(v)} data-testid={`variant-${v.name.toLowerCase()}-button`}><span>{v.name}</span><strong>₹{v.price}</strong></Button>)}</div></Modal>;
}

/**
 * Quantity entry for a cart line.
 *
 * The keypad stays whole-number — that is what it is for — and the half plate gets
 * its own key: ½ turns whatever is on the display into that many-and-a-half plates
 * (blank or 0 becomes 0.5). Pressing it twice is idempotent, so a captain who taps
 * it out of habit cannot build 1.55.
 *
 * A digit pressed straight after ½ REPLACES the display rather than appending to
 * it. Appending composed nonsense — ½ then 2 read as "0.52", which the server takes
 * as half a plate and a bit — and the captain's obvious next move after tapping the
 * wrong key is to type the number they actually wanted.
 *
 * Two different floors, deliberately: a TYPED value floors at MIN_QTY — clamping a
 * legal 0.25 up to 0.5 doubled a quarter portion silently — while an EMPTY or
 * zeroed pad falls back to half a plate, the smallest thing anyone orders on
 * purpose. Dropping a line to zero is still a void, and voiding has its own
 * dialog because the kitchen may already be cooking it.
 */
export function NumberPad({ item, onSave, onClose }) {
  const [value, setValue] = useState(fmtQty(item.qty));
  // True while the display holds a fraction nobody can meaningfully append to.
  const [fractional, setFractional] = useState(!Number.isInteger(roundQty(item.qty)));
  const press = key => {
    if (key === "C") { setValue(""); setFractional(false); return; }
    if (key === "⌫") { setValue(current => current.slice(0,-1)); setFractional(false); return; }
    setValue(current => fractional ? key : `${current}${key}`);
    setFractional(false);
  };
  const half = () => { setValue(current => fmtQty(Math.trunc(roundQty(current)) + HALF_QTY)); setFractional(true); };
  return <Modal title={`Quantity · ${item.name}`} onClose={onClose} testId="quantity-numpad-modal"><div className="numpad-value" data-testid="quantity-numpad-value">{value}</div><div className="numpad">{[1,2,3,4,5,6,7,8,9,"C",0,"⌫"].map(key => <button key={key} onClick={() => press(String(key))} data-testid={`numpad-${key}-button`}>{key}</button>)}</div><button className="numpad-half" onClick={half} data-testid="numpad-half-button">½ Half plate</button><Button variant="action" className="primary-wide" onClick={() => { const typed = roundQty(value); onSave(typed > 0 ? Math.max(MIN_QTY, typed) : HALF_QTY); }} data-testid="quantity-save-button">Update quantity</Button></Modal>;
}